import app from '../../authentication/firebaseStart';
import { getKioskDevices } from '../../data/FirebaseUtil';

const firestore = app.firestore();

// Helper function to execute queries and get total count
export const getTotalCount = async (context, { searchText, searchField, orderDirection }) => {
  try {
    if (searchText && searchField) {
      // For search queries, get the total count of matching documents
      const totalQuery = await createQuery({ 
        context, 
        searchText, 
        searchField,
        orderBy: searchField,
        orderDirection 
      });
      // This is needed for pagination to work correctly when going to last page. As we are using old version of firebase we don't have count() method to directly get the count for the query
      const countSnapshot = await totalQuery.get();
      return countSnapshot.size;
    } else {
      // For non-search queries, use the total user count
      return await getDeviceCount(
        context.currentUser.claims.companyID, 
        context.currentUser.claims.isSuperAdmin
      );
    }
  } catch (error) {
    console.error('Error getting total count:', error);
    throw error;
  }
};

const getDeviceStatus = (device) => {
  if(device?.deviceState === 3) {
    if(!device?.checkOutState && device?.isCharging === true) {
      return "Available"
    }
    if(device?.checkOutState === true && !device?.isCharging) {
      return "In Use"
    }
  } else if([1,2].includes(device?.deviceState) && !device?.checkOutState && !device?.isCharging) {
    return "Missing"
  }
  return ""
}

// Rest of the helper functions remain the same
const executeQuery = async (query) => {
  const snapshot = await query.get();
  const docs = snapshot.docs;
  return {
    docs: docs,
    devices: docs.map(doc => ({
      __name__: doc.id,
      deviceStatusText: getDeviceStatus(doc.data()),
      ...doc.data()
    })),
    cursors: {
      first: docs[0] || null,
      last: docs[docs.length - 1] || null
    }
  };
};

// Apply pagination to query
const applyPagination = (query, { startAfterDoc, endBeforeDoc, startAtDoc, limit }) => {
  let paginatedQuery = query;

  if (startAtDoc) {
    paginatedQuery = paginatedQuery.startAt(startAtDoc);
  } else if (startAfterDoc) {
    paginatedQuery = paginatedQuery.startAfter(startAfterDoc);
  } else if (endBeforeDoc) {
    paginatedQuery = paginatedQuery.endBefore(endBeforeDoc);
  }

  if (limit) {
    paginatedQuery = endBeforeDoc ? 
      paginatedQuery.limitToLast(limit) : 
      paginatedQuery.limit(limit);
  }

  return paginatedQuery;
};

const getKioskNamesList = async (context) => {
  const { companyID, isManager, siteName } = context.currentUser.claims;
  let kioskQuery = firestore
      .collection('tenants')
      .doc(companyID)
      .collection('Kiosk')
    if (isManager && siteName) {
      kioskQuery = kioskQuery.where('siteName', '==', siteName);
    }
    const kiosks = await kioskQuery.get();
    return kiosks.docs.map(doc => doc?.id)
}

// Create query with search and filters
const createQuery = async ({ context, searchText, searchField, orderBy, orderDirection }) => {
  const { companyID, isManager, siteName } = context.currentUser.claims;
  let kioskQuery = firestore
      .collection('tenants')
      .doc(companyID)
      .collection('Kiosk')
    if (isManager && siteName) {
      kioskQuery = kioskQuery.where('siteName', '==', siteName);
      }
    const kioskSnapshot = await kioskQuery.get();
    const kisokNamesList = kioskSnapshot.docs.map(doc => doc?.id)
    
    let query = firestore
    .collection('tenants')
    .doc(companyID)
    .collection('Devices')
    .where('kioskName', 'in', kisokNamesList);

    if (isManager && siteName) {
      query = query.where('siteName', '==', siteName);
     }

  if (searchText && searchField) {
    const searchTerm = searchText.trim();
    const endTerm = searchTerm + '\uf8ff';
    
    query = query
      .where(searchField, '>=', searchTerm)
      .where(searchField, '<=', endTerm)
      .orderBy(searchField, orderDirection);
  } else {
    query = query.orderBy(orderBy, orderDirection);
  }

  return query;
};

// Main function to fetch users with pagination
export const fetchDevices = async ({
  limit,
  startAfterDoc = null,
  endBeforeDoc = null,
  startAtDoc = null,
  orderBy = 'lastUpdatedAt',
  orderDirection = 'desc',
  searchText = null,
  searchField = null,
  context,
  totalCount = null,
  isFirstPage = false,
  isLastPage = false
}) => {
  console.log(`Fetching devices with params:`, {
    limit,
    orderBy,
    orderDirection,
    searchText,
    searchField,
    totalCount,
    hasStartAfter: !!startAfterDoc,
    hasEndBefore: !!endBeforeDoc,
    hasStartAt: !!startAtDoc
  });

  try {
    const { companyID, isManager, siteName } = context.currentUser.claims;
    const kioskNamesList = await getKioskNamesList(context);
    const deviceResult = await getKioskDevices({context, 
      searchText, 
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection,startAfterDoc,
      endBeforeDoc,
      startAtDoc,
      limit,
      companyID, 
      isManager, 
      siteName,
      kioskNamesList,
      fromAllData: true,
      deviceStates:[1,2,3],
      isFirstPage,
      isLastPage
     })
     if (!deviceResult?.status) {
      return {
        devices: [],
        totalCount,
        cursors: {
          first: null,
          last: null
        }
      };
    }

    const devices = deviceResult?.data?.map(doc => ({
      deviceStatusText: getDeviceStatus(doc),
      ...doc
    }))

    let count;
    if(totalCount > 0) {
      count = totalCount;
    } else {
      const list =  await getKioskDevices({ 
        searchText, 
        searchField,
        orderBy: searchText ? searchField : orderBy,
        orderDirection,startAfterDoc,
        companyID, 
        isManager, 
        siteName,
        kioskNamesList,
        fromAllData: true,
        deviceStates:[1,2,3]
       });
       count = list?.data?.length || 0;
    }
    
    return {
      devices,
      totalCount: count,
      cursors: {first: devices[0]?.__name__ || null,
        last: devices[devices.length - 1]?.__name__ || null}
    };
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

export const fetchLastPage = async ({
  context,
  limit,
  searchText = null,
  searchField = null,
  orderBy = 'lastUpdatedAt',
  orderDirection = 'desc',
  totalCount = null
}) => {
  try {
    const count = totalCount ?? await getTotalCount(context, { searchText,searchField,orderDirection });

    const totalPages = Math.floor(count / limit);
    const remainingRecords = count - (totalPages * limit);
    const lastPageLimit = remainingRecords > 0 ? remainingRecords : limit;

    const query = await createQuery({
      context,
      searchText,
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection
    });

    const snapshot = await query.limitToLast(lastPageLimit).get();

    return {
      users: snapshot.docs.map(doc => ({
        __name__: doc.id,
        ...doc.data()
      })),
      cursors: {
        first: snapshot.docs[0] || null,
        last: snapshot.docs[snapshot.docs.length - 1] || null
      },
      totalCount: count
    };
  } catch (error) {
    console.error('Error fetching last page:', error);
    throw error;
  }
};

// Get total user count
export const getDeviceCount = async (companyID, isSuperAdmin) => {
  try {
    const doc = await firestore
      .collection('tenants')
      .doc(companyID)
      .collection('TotalCounts')
      .doc('Devices')
      .get();

    const totalCount = doc.data()?.count || 0;
    return isSuperAdmin ? totalCount : totalCount - 1;
  } catch (error) {
    console.error('Error fetching user count:', error);
    throw error;
  }
};

// Get application settings
export const getAppSettings = async (context) => {
  try {
    const companyID = context.currentUser.claims.companyID;
    const doc = await firestore
      .collection('tenants')
      .doc(companyID)
      .collection('Configuration')
      .doc('AppSettings')
      .get();

    return doc.data();
  } catch (error) {
    console.error('Error fetching app settings:', error);
    throw error;
  }
};


export default {
  fetchDevices, getTotalCount, fetchLastPage, getAppSettings
};